import Vue from 'vue'
import Router from 'vue-router'
import store from '@/store/store'
import ProjectTemplate from '@/components/ProjectTemplate'
import ChildView from '@/components/ChildView'
import UserService from '@/services/user.service'
import {actionTypes} from '@/store/actionTypes'
import AuthService from '@/services/auth.service'
import {BASE_PATH} from '@/utils/base-path'

Vue.use(Router)

let router = new Router({
    mode: 'history',
    base: BASE_PATH,
    routes: [
        {
            path: '/',
            name: 'default',
            redirect: '/dashboard',
            meta: {requiresAuth: true}
        },
        {
            path: '/home',
            name: 'home',
            redirect: '/authentication'
        },
        {
            path: '/authentication',
            component: ChildView,
            children: [
                {
                    path: '',
                    name: 'AuthenticationOptions',
                    component: () => import('@/views/Authentication/AuthenticationOptions.vue')
                },
                {
                    path: 'failed',
                    name: 'AuthenticationFailed',
                    component: () => import('@/views/Authentication/AuthenticationFailed.vue')
                },
                {
                    path: 'self',
                    name: 'AuthenticationSelf',
                    component: () => import('@/views/Authentication/AuthenticationSelf.vue')
                },
                {
                    path: 'fpt-id',
                    component: ChildView,
                    children: [
                        {
                            path: '',
                            name: 'AuthenticationFptId',
                            component: () => import('@/views/Authentication/AuthenticationFptId.vue')
                        },
                        {
                            path: 'success',
                            name: 'AuthenticationFptIdSuccess',
                            component: () => import('@/views/Authentication/AuthenticationFptIdSuccess.vue')
                        }
                    ]
                }
            ]
        },
        {
            path: '/dashboard',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    component: () => import('@/views/Dashboard/Dashboard')
                }
            ]
        },
        {
            path: '/campaign',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    component: () => import('@/views/Campaign/CampaignList')
                },
                {
                    path: 'data',
                    name: 'sales',
                    component: () => import('@/views/Campaign/CampaignData')
                },
                {
                    path: 'form',
                    name: 'CampaignForm',
                    component: () => import('@/views/Campaign/CampaignForm')
                },
                {
                    path: ':campaignId',
                    name: 'viewCampaign',
                    component: () => import('@/views/Campaign/CampaignDetails')
                },
                {
                    path: ':campaignId/test-call-inbound',
                    name: 'TestCallInbound',
                    component: () => import('@/views/Campaign/TestCallInbound')
                }
            ]
        },
        {
            path: '/scenarios',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    name: 'ScenarioList',
                    component: () => import('@/views/Scenario/ScenarioList')
                },
                {
                    path: ':scenarioId',
                    name: 'ScenarioContentEditor',
                    component: () => import('@/views/Scenario/ScenarioContentEditor')
                },
                {
                    path: ':scenarioId/history',
                    name: 'ScenarioHistory',
                    component: () => import('@/views/Scenario/ScenarioHistory')
                },
                {
                    path: ':scenarioId/audit',
                    name: 'ScenarioAudit',
                    component: () => import('@/views/Scenario/ScenarioAudit')
                },
                {
                    path: ':scenarioId/testsets',
                    name: 'ScenarioExampleList',
                    component: () => import('@/views/Scenario/example/ScenarioTestSet')
                },
                {
                    path: ':scenarioId/testsets/:groupName',
                    name: 'ScenarioExampleTest',
                    component: () => import('@/views/Scenario/example/ScenarioAutomationTest')
                }
            ]
        },
        {
            path: '/debug',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    component: () => import('@/modules/fptai/views/NotFound/NotFound')
                },
                {
                    path: ':scenarioId',
                    name: 'Debug',
                    component: () => import('@/views/Debug/DebugView.vue')
                }
            ]
        },
        {
            path: '/qc-campaign',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    component: () => import('@/views/QualityControl/QCCampaignList')
                },
                {
                    path: ':QCCampaignId',
                    name: 'viewQCCampaign',
                    component: () => import('@/views/QualityControl/QCCampaignDetails')
                }
            ]
        },
        {
            path: '/agents',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            children: [
                {
                    path: '',
                    component: () => import('@/views/Agent/AgentList')
                },
                {
                    path: ':agentId',
                    name: 'viewAgent',
                    component: () => import('@/views/Agent/AgentDetail')
                },
                {
                    path: ':agentId/history',
                    name: 'AgentHistory',
                    component: () => import('@/views/Agent/AgentHistory')
                },
            ]
        },
        {
            path: '/admin',
            component: ProjectTemplate,
            meta: {requiresAuth: true},
            roles: ['admin'],
            children: [
                {
                    path: 'campaigns',
                    name: 'AdminCampaignList',
                    component: () => import('@/views/Admin/AdminCampaignList')
                }
            ]
        },
        {
            path: '/access-denied',
            name: 'AccessDenied',
            component: () => import('@/modules/fptai/views/AccessDenied/AccessDenied')
        },
        {
            path: '**',
            component: () => import('@/modules/fptai/views/NotFound/NotFound')
        }
    ]
})

router.beforeEach(async (to, from, next) => {   // NOSONAR
    let guard = true
    if (localStorage.getItem('debug')) {
        console.log(JSON.stringify({
            from: from.path,
            to: to.path,
            requiresAuth: to.matched.some(record => record.meta.requiresAuth),
            token: AuthService.getToken()?.split('.').at(-1) ?? 'null',
            'store.getters.token': store.getters.token?.split('.').at(-1) ?? 'null',
        }))
    }
    // authentication
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!store.getters.token) {
            guard = false
            store.dispatch(actionTypes.user.SIGN_IN)
        }
    }
    if (!guard) {
        return
    }
    if (store.getters.token) {
        // role
        let userRoles = null
        if (to.matched.some(record => Array.isArray(record.meta.roles) && record.meta.roles.length > 0)) {
            if (store.state.user.isProfileLoaded) {
                userRoles = store.state.user.profile.roles
            } else {
                try {
                    let profileRes = await UserService.getProfile()
                    userRoles = profileRes.data.roles
                } catch (e) {
                    router.push('/')
                }
            }
            if (Array.isArray(userRoles)) {
                for (let record of to.matched) {
                    let routeRoles = record.meta.roles
                    if (Array.isArray(routeRoles) && routeRoles.length > 0) {
                        if (_.difference(userRoles, routeRoles).length === userRoles.length) {
                            guard = false
                            router.push({name: 'AccessDenied'})
                            break
                        }
                    }
                }
            } else {
                guard = false
            }
        }
        // new feature
        if (to.matched.some(record => record.meta.newFeature)) {
            if (store.state.user.isProfileLoaded) {
                userRoles = store.state.user.profile.roles
            } else {
                try {
                    let profileRes = await UserService.getProfile()
                    userRoles = profileRes.data.roles
                } catch (e) {
                    router.push('/')
                }
            }
            if (userRoles && !userRoles.includes('admin')) {
                if (process.env.VUE_APP_NEW_FEATURE !== 'enable') {
                    guard = false
                }
            }
        }
    }

    // action
    if (guard) {
        next()
    } else {
        router.push({name: 'AccessDenied'})
    }
})

export default router
